import { useConfirm } from "../confirm/ConfirmProvider";
import { toast } from "../toast/toast";
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaBars } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../components/Loading";
import PasswordChangePopup from "../components/PasswordChangePopup";
import { useUser } from "../contexts/UserContext";
import { deleteSession } from "../fetch/UserRequest";
import { getLastLab } from "../function/lastLab";
import style from "../styles/modules/Header.module.css";

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const prevIsMobile = useRef(isMobile);
  const accountRef = useRef<HTMLLIElement>(null);

  const { user, userLabs, memberships, alertCount, setUser } = useUser();
  const confirm = useConfirm();
  const navigate = useNavigate();

  // ホーム：最後に選んだ研究室が今も所属にあればそこへ、なければ研究室選択へ。
  function goHome() {
    const last = user ? getLastLab(user.id) : null;
    if (last !== null && userLabs.some((l) => l.id === last)) {
      navigate("/reagents", { state: { labId: last } });
    } else {
      navigate("/labs");
    }
  }

  // 鍵管理：ホームと同じく、最後に選んだ研究室を引き継いで開く。
  function goKeys() {
    setMenuOpen(false);
    const last = user ? getLastLab(user.id) : null;
    if (last !== null && userLabs.some((l) => l.id === last)) {
      navigate("/keys", { state: { labId: last } });
    } else {
      navigate("/labs");
    }
  }

  //ウィンドウサイズを習得し、閾値を超えたらisMobileを切り替える
  useEffect(() => {
    const handleRereagentSize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    window.addEventListener("resize", handleRereagentSize);
    return () => window.removeEventListener("resize", handleRereagentSize);
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    chackIsAdmin();
  }, [user, memberships]);

  useEffect(() => {
    if (prevIsMobile.current !== isMobile && menuOpen === true) {
      setMenuOpen(!menuOpen);
    }

    prevIsMobile.current = isMobile;
  }, [isMobile]);

  // アカウントメニューの外側クリックで閉じる
  useEffect(() => {
    if (!accountOpen) return;
    const onDown = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [accountOpen]);

  async function logout() {
    try {
      if (
        !(await confirm("ログアウトします。よろしいですか？", {
          confirmText: "ログアウト",
        }))
      ) {
        return;
      }
      setIsLoading(true);

      if (user) {
        // セッション削除APIは認証が必要なため、先に実行してから
        // ローカルのセッションIDを破棄する（順序が逆だと401になる）
        await deleteSession({ user: user });
        localStorage.removeItem("sessionId");
        setUser(null);
        navigate("/login");
      } else {
        throw new Error("ログイン中ユーザーの取得に失敗しました。");
      }
    } catch (error) {
      toast.error("ログアウト処理に失敗しました。");
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function chackIsAdmin() {
    setIsLoading(true);

    // 設定は大学管理者(tenant_admin)専用
    const isAdmin =
      Array.isArray(memberships) &&
      memberships.some((item) => item.role_code === "tenant_admin");

    setIsAdmin(isAdmin);
    setIsLoading(false);
  }

  const HeaderList: React.FC = () => {
    return (
      <ul>
        <li>
          <a onClick={goHome}>ホーム</a>
        </li>
        <li>
          <a className={style.noticePoint} onClick={() => navigate("/alerts")}>
            アラート
            {alertCount > 0 && <span>●</span>}
          </a>
        </li>
        <li>
          <a onClick={goKeys}>鍵管理</a>
        </li>
        {isAdmin && (
          <li>
            <a onClick={() => navigate("/admin")}>設定</a>
          </li>
        )}
        {!isMobile ? (
          <li className={style.account} ref={accountRef}>
            <a onClick={() => setAccountOpen((o) => !o)}>
              {user?.name ?? "アカウント"} ▾
            </a>
            {accountOpen && (
              <div className={style.accountMenu}>
                <button
                  onClick={() => {
                    setAccountOpen(false);
                    setShowPasswordPopup(true);
                  }}
                >
                  パスワード変更
                </button>
                <button
                  onClick={() => {
                    setAccountOpen(false);
                    logout();
                  }}
                >
                  ログアウト
                </button>
              </div>
            )}
          </li>
        ) : (
          <>
            <li>
              <a
                onClick={() => {
                  setMenuOpen(false);
                  setShowPasswordPopup(true);
                }}
              >
                パスワード変更
              </a>
            </li>
            <li>
              <a onClick={logout}>ログアウト</a>
            </li>
          </>
        )}
      </ul>
    );
  };

  return (
    <>
      <header className={style.header}>
        <h2>試薬管理システム</h2>
        {!isMobile && (
          <nav>
            <HeaderList />
          </nav>
        )}
        {isMobile && (
          <button
            className={style.menuBtn}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            {menuOpen ? <AiOutlineClose /> : <FaBars />}
          </button>
        )}
      </header>

      {menuOpen && (
        <div
          className={style.mobileMenuOverlay}
          onClick={() => setMenuOpen(false)}
        >
          <div
            className={style.mobileMenu}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>メニュー</h2>
            <nav>
              <HeaderList />
            </nav>
          </div>
        </div>
      )}

      {showPasswordPopup && (
        <PasswordChangePopup onClose={() => setShowPasswordPopup(false)} />
      )}

      {isLoading && <LoadingOverlay />}
    </>
  );
};
export default Header;
